/**
 * Corporate pricing helpers.
 *
 * Contentful has a single `iotAccountType` dropdown whose Corporate option
 * is split in the UI into two pricing variants via a secondary dropdown
 * `iotCorporateType`:
 *
 *   - Corporate - Standard Pricing  => form/OFD behaves like
 *                                      Business Regular Medium
 *   - Corporate - Custom Pricing    => existing Corporate flow
 *                                      (hide prices, hide contract step,
 *                                       hide contract PDF attachments, etc.)
 *
 * The Contentful form engine only supports AND semantics on
 * `appearsAfterField` + `appearsAfterValue`, so the Standard-Corporate ->
 * BRM alias cannot be expressed in Contentful and has to live in code.
 * Keep it here in one place so the rule is stated once.
 *
 * Legacy Corporate orders (saved before `iotCorporateType` existed) have
 * `iotCorporateType === undefined`, which these helpers treat as
 * Custom-Pricing -- preserving today's production behavior.
 */

/**
 * Translate a raw iotAccountType value to its "effective" account type
 * for BRM-dependent logic. Returns BRM when Corporate+Standard is
 * selected; otherwise returns the raw value unchanged.
 *
 * @param {string|undefined} rawAccountType   Current iotAccountType value.
 * @param {string|undefined} corporateType    Current iotCorporateType value.
 * @param {object} values                      Localized contentful values.
 * @param {string} values.valueCorporate
 * @param {string} values.valueCorporateStandardPricing
 * @param {string} values.valueBusinessRegularMedium
 */
export const getEffectiveAccountType = (
  rawAccountType,
  corporateType,
  {
    valueCorporate,
    valueCorporateStandardPricing,
    valueBusinessRegularMedium
  }
) => {
  if (
    rawAccountType === valueCorporate &&
    corporateType === valueCorporateStandardPricing
  )
    return valueBusinessRegularMedium;
  return rawAccountType;
};

/**
 * True when the order is Corporate AND the Custom-Pricing variant applies
 * (either Custom is explicitly selected or iotCorporateType is missing,
 * which is the legacy case -- legacy Corporate orders default to the
 * existing Custom-Pricing flow to preserve production behavior).
 */
export const isCustomPricingCorporate = (
  accountType,
  corporateType,
  { valueCorporate, valueCorporateStandardPricing }
) =>
  accountType === valueCorporate &&
  corporateType !== valueCorporateStandardPricing;
